import { Dispatch, SetStateAction, memo, ComponentType, useMemo } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import InvitePeople from "@atlassian/invite-people";
import {
    OnSubmitData,
    SubmitButtonRef
} from "@atlassian/invite-people/dist/types/types";

import { useSearchParameters } from "@/components/signup/hooks/use-search-parameters";

import { useCloudStatus } from "../../../../../../hooks/use-cloud-status";
import { ANALYTICS_PREFIX } from "../../../../common/constants.tsx";
import {
    onInviteUsersRedirectFailureInvite,
    onInviteUsersRedirectSuccessInvite
} from "../../../../utils/redirect-logging.ts";
import { useJfind1663TaskPolling } from "../hooks/use-jfind-1663-create-project-task-polling-context.tsx";

import messages from "./messages.tsx";

type InvitePeopleWrapperProps = {
    submitRef: SubmitButtonRef | undefined;
    onClickNext?: () => void;
    onInviteStart: () => void;
    setEmailList: Dispatch<SetStateAction<string[]>>;
    showEmailInput?: boolean;
};

export const InvitePeopleWrapper = ({
    submitRef,
    onClickNext,
    onInviteStart,
    setEmailList,
    showEmailInput
}: InvitePeopleWrapperProps) => {
    const { formatMessage } = useIntl();
    const { cloudId } = useCloudStatus();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { taskIdParam } = useSearchParameters();
    const { pollTaskByTaskIdQueryParam } = useJfind1663TaskPolling();

    const resourceAri = `ari:cloud:jira::site/${cloudId}`;

    const handleInviteeListFocused = () => {
        createAnalyticsEvent({
            action: "focused",
            actionSubject: "component",
            actionSubjectId: `${ANALYTICS_PREFIX}InvitePeopleFocus`,
            eventType: "ui"
        }).fire();
    };

    const handleSubmit = async (data: OnSubmitData) => {
        const inviteData = await Promise.resolve(data);

        const {
            invitePeopleData: { users },
            defaultOnSubmit
        } = inviteData;

        if (users.length) {
            onInviteStart();

            await pollTaskByTaskIdQueryParam();

            // calls the defaultOnSubmit to send invites and other related tasks (e.g. RECAPTCHA validation)
            try {
                await defaultOnSubmit();
                await onInviteUsersRedirectSuccessInvite();
            } catch (error) {
                await onInviteUsersRedirectFailureInvite(error);
            }
        }

        if (onClickNext) {
            onClickNext();
        }
    };

    // We found that this was not preventing the infinite re-renders as intended, which is why the inner React.memo was added.
    // See https://hello.jira.atlassian.cloud/browse/CONFONBO-4737
    const EmailInput: ComponentType = useMemo(
        () =>
            // eslint-disable-next-line react/no-unstable-nested-components
            memo(() => (
                <div css={heightAutoFixStyle}>
                    <InvitePeople
                        /* Misc. */
                        source="jira-onboarding-wizard-invites-page"
                        isInitialLoad
                        userRole="basic" // removes product selector
                        resourceAri={resourceAri}
                        /* UI overrides */
                        disableNoInviteError
                        hideFormHeader
                        hideCancelButton
                        inviteeListLabelText={formatMessage(
                            messages.emailShare
                        )}
                        inviteeListPlaceholderText={formatMessage(
                            messages.emailSharePlaceholder
                        )}
                        inviteeListRequired={false}
                        experimental_submitButtonRef={submitRef}
                        /* Callbacks and lifecycle methods */
                        onInviteeListFocus={handleInviteeListFocused}
                        experimental_onInviteeListChangeHandler={setEmailList}
                        onSubmit={handleSubmit}
                    />
                </div>
            )),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    if (showEmailInput) {
        return <EmailInput />;
    }

    // falsy taskId means an error occurred during project creation, which is a prerequisite for inviting users
    if (!taskIdParam) {
        return null;
    }

    return <EmailInput />;
};

const heightAutoFixStyle = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ['div[data-testid="testId-invite-people-invitee-list"]']: {
        height: "fit-content !important"
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ['div[data-testid="testId-invite-people-form"] > div']: {
        maxWidth: "unset !important"
    }
});
